/** «ЗП» — ровно то, что отдаёт `GET /api/salary/weeks`. */
import { apiFetch, query } from "@/lib/api";
import type { Place } from "@/lib/bonus";

export type SalaryWeek = {
  year: number;
  week: number;
  from: string;
  to: string;
  /** Неделя ещё идёт: часы и гарантию CRM начислит в понедельник. */
  open: boolean;
  /** Минут в акцепте. */
  minutes: number;
  shifts: number;
  days: { date: string; minutes: number; total: number }[];
  /** Начислено за неделю, как считает legacy `zpCalc`. */
  total: number;
  /** Вычеты, которые не поместились в премии (≤ 0): legacy их не удерживает. */
  minus_left: number;
  hours: number;
  salary: number;
  bonus: number;
  per_hour: number | null;
  /** Часы × гарантированная ставка; null — гарантии нет или часов меньше минимума. */
  guarantee_floor: number | null;
  /** Заявки на выплату ЗП за эту неделю; null — заявки нет. */
  paid: number | null;
};

export type SalaryModule = {
  id: number;
  name: string;
  rule: string | null;
  manual: boolean;
  /** По неделям, в том же порядке, что `weeks`. */
  totals: number[];
  counts: number[];
};

export type SalarySection = {
  id: "hours" | "salary" | "bonus";
  name: string;
  totals: number[];
  categories: { id: number; name: string; totals: number[]; modules: SalaryModule[] }[];
};

export type Salary = {
  user: Place & { rate: number };
  guarantee: { min_hours: number; price: number } | null;
  weeks: SalaryWeek[];
  sections: SalarySection[];
  /** Можно ли смотреть чужую зарплату; без права `staff` и `locations` пусты. */
  can_all: boolean;
  staff: Place[];
  locations: Place[];
};

export const WEEKS = [4, 8, 12, 26];
export const DEFAULT_WEEKS = 4;

export function loadSalary(p: { weeks: number; to: string | null; user: number | null; location: number }): Promise<Salary> {
  return apiFetch<Salary>(`/api/salary/weeks${query(p)}`);
}

/** Период вкладки «Ставка в час» — `GET /api/salary/rate`, модуль `billing/salary_rate.py`. */
export type RatePeriod = {
  from: string;
  to: string;
  weeks: number[];
  minutes: number;
  shifts: number;
  total: number;
  /** Доплата до гарантии за период. */
  guarantee: number;
  /** Эффективная ставка: всё начисленное ÷ часы акцепта. */
  per_hour: number | null;
  /** То же без доплаты до гарантии — сколько заработал часами и премиями сам. */
  own_per_hour: number | null;
  bonus_per_hour: number | null;
  /** Ставка часа и гарантия последней недели периода — по проводкам. */
  base_rate: number | null;
  guarantee_rate: number | null;
  guarantee_share: number | null;
};

export type RateRow = {
  user: Place & { rate: number };
  guarantee: { min_hours: number; price: number } | null;
  periods: RatePeriod[];
  changes: { kind: "base_rate" | "guarantee_rate"; week: number; from_date: string; was: number; now: number }[];
  delta: { per_hour: number; per_hour_pct: number; own_per_hour: number | null } | null;
};

export type SalaryRate = { rows: RateRow[]; whole: boolean; can_all: boolean; staff: Place[]; locations: Place[] };

export const RATE_WEEKS = [8, 12, 26];
export const DEFAULT_RATE_WEEKS = 12;

export function loadSalaryRate(p: { users: number[]; weeks: number; to: string | null; location: number }): Promise<SalaryRate> {
  const users = p.users.map((u) => `&users=${u}`).join("");
  return apiFetch<SalaryRate>(`/api/salary/rate${query({ weeks: p.weeks, to: p.to, location: p.location })}${users}`);
}
